import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getRequestIP, getRequestHeader } from "@tanstack/react-start/server";
import { createHash } from "crypto";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

function detectDevice(ua: string): "mobile" | "tablet" | "desktop" {
  const s = ua.toLowerCase();
  if (/ipad|tablet|(android(?!.*mobile))/.test(s)) return "tablet";
  if (/mobi|iphone|ipod|android.*mobile|windows phone/.test(s)) return "mobile";
  return "desktop";
}

/** Record a page view. Public — counts real visitors by hashed IP per day. */
export const trackPageView = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({
      path: z.string().min(1).max(255),
      referrer: z.string().max(512).optional().nullable(),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const ip = getRequestIP({ xForwardedFor: true }) ?? "0.0.0.0";
    const ua = getRequestHeader("user-agent") ?? "";
    const device = detectDevice(ua);
    const day = new Date().toISOString().slice(0, 10);
    const visitor_id = createHash("sha256").update(`${ip}|${ua}|${day}`).digest("hex").slice(0, 32);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // One real visit per visitor + path + day — refreshes/double-mounts are ignored.
    await supabaseAdmin.from("page_views").upsert(
      {
        path: data.path,
        device,
        visitor_id,
        view_date: day,
        referrer: data.referrer ?? null,
      },
      { onConflict: "visitor_id,path,view_date", ignoreDuplicates: true },
    );
    return { ok: true };
  });

type Row = { path: string; device: string; visitor_id: string; created_at: string };

/** Aggregated traffic analytics for the admin dashboard. Admin only. */
export const getAnalytics = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden: admin role required");

    const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await context.supabase
      .from("page_views")
      .select("path, device, visitor_id, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);

    const rows = (data ?? []) as Row[];
    const todayStr = new Date().toISOString().slice(0, 10);

    const uniq = new Set<string>();
    const uniqToday = new Set<string>();
    const device = { desktop: 0, mobile: 0, tablet: 0 };
    const pathCounts: Record<string, number> = {};
    let viewsToday = 0;

    // last 14 days buckets
    const days: { date: string; views: number; visitors: number }[] = [];
    const dayIndex: Record<string, number> = {};
    for (let i = 13; i >= 0; i--) {
      const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      dayIndex[d] = days.length;
      days.push({ date: d, views: 0, visitors: 0 });
    }
    const daySeen: Record<number, Set<string>> = {};

    for (const r of rows) {
      uniq.add(r.visitor_id);
      const dev = (r.device as keyof typeof device) in device ? (r.device as keyof typeof device) : "desktop";
      device[dev]++;
      pathCounts[r.path] = (pathCounts[r.path] ?? 0) + 1;
      const day = r.created_at.slice(0, 10);
      if (day === todayStr) { viewsToday++; uniqToday.add(r.visitor_id); }
      const idx = dayIndex[day];
      if (idx !== undefined) {
        days[idx].views++;
        (daySeen[idx] ??= new Set()).add(r.visitor_id);
      }
    }
    for (const [idxStr, set] of Object.entries(daySeen)) days[Number(idxStr)].visitors = set.size;

    const topPages = Object.entries(pathCounts)
      .map(([path, count]) => ({ path, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);

    return {
      totalViews: rows.length,
      uniqueVisitors: uniq.size,
      viewsToday,
      visitorsToday: uniqToday.size,
      device,
      days,
      topPages,
    };
  });
